import { type ActionFunctionArgs, json } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import { useCallback } from 'react';
import { z } from 'zod';
import { requireConfirmationDialog } from '~/components/confirm-dialog.js';
import { ListingReportedEmail } from '~/components/emails/listing-reported.email.server.tsx';
import { badRequest } from '~/utils/responses.server.ts';

const paramsSchema = z.object({
  id: z.string(),
});

export async function action({ context, params }: ActionFunctionArgs) {
  const paramsResult = paramsSchema.safeParse(params);

  if (!paramsResult.success) {
    throw badRequest({});
  }

  const user = await context.authSession.requireUser();

  const listing = await context.db.listing.findFirst({
    where: { id: paramsResult.data.id },
    select: {
      id: true,
      userId: true,
    },
  });

  if (listing) {
    await context.email.sendEmail({
      to: 'report-listing@joinhostu.com',
      subject: 'New listing reported',
      react: (
        <ListingReportedEmail
          listingId={listing.id}
          listingOwnerId={listing.userId}
          reportedById={user.id}
        />
      ),
    });
  }

  return json({ ok: true });
}

export const useReportListing = (listingId: string) => {
  const { submit } = useFetcher({ key: `report-listing-${listingId}` });

  const report = useCallback(() => {
    requireConfirmationDialog({
      text: 'Are you sure you want to report this listing?',
      actionButtonText: 'Report',
      onConfirm: () => {
        submit(
          {},
          {
            method: 'POST',
            action: `/resources/listing/${listingId}/report`,
          },
        );
      },
    });
  }, [submit, listingId]);

  return { report };
};
